"use client";

import { useCallback, type ReactNode } from "react";
import { useAuth } from "@/components/auth-provider";
import { useAuthDialog } from "@/components/auth-dialog-provider";
import { ShieldCheck } from "lucide-react";

interface LoginGateOptions {
  /** 弹窗标题 */
  title: string;
  /** 标题下方说明文案(为何需要登录) */
  description?: string;
  /** 标题图标,默认盾牌(强调安全/防爬虫) */
  icon?: ReactNode;
}

/**
 * 通用登录引导门 hook。
 *
 * 返回:
 * - `requireLogin(action)`: 已登录直接执行 action;未登录则弹出全局登录框,
 *   登录成功后自动执行 action(补完被拦截的动作,如关注名人、打开 AI 对话、导航历史)。
 * - `dialog`: 始终为 null（登录弹窗已由全局 AuthDialogProvider 统一挂载，保留字段仅为向后兼容）。
 * - `isLoggedIn` / `loading`: 当前鉴权状态。
 *
 * 用于 AI 助手、1 年前历史、名人关注 等需要登录才能使用的入口。
 * 采用 hook 而非"包裹子元素"形式,是因为被包裹的按钮自带 onClick,
 * 包裹层的拦截无法先于子元素 onClick 触发。
 *
 * 重构说明：原先每个调用点各自挂载 Dialog 实例，首页列表几十个 AffinityChip
 * 产生几十个 Dialog，焦点互相干扰。现统一委托给全局 AuthDialogProvider 单例。
 */
export function useLoginGate(opts: LoginGateOptions) {
  const { user, loading } = useAuth();
  const { openAuth } = useAuthDialog();

  const requireLogin = useCallback(
    (action: () => void) => {
      if (loading) return;
      if (user) {
        action();
        return;
      }
      openAuth({
        title: opts.title,
        description: opts.description,
        icon: opts.icon ?? <ShieldCheck className="w-5 h-5" />,
        onSuccess: action,
      });
    },
    [user, loading, openAuth, opts.title, opts.description, opts.icon]
  );

  // 登录弹窗已全局统一挂载，此处返回 null 保持调用点 `dialog` 字段向后兼容
  const dialog: ReactNode = null;

  return { requireLogin, dialog, isLoggedIn: !!user, loading };
}
